'use client'

import { FC, useMemo, useRef } from 'react'
import { SpaceTask, SpaceSprint } from '@/types/spaces'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { IconCheckbox, IconCalendar } from '@tabler/icons-react'
import {
  addDays,
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfMonth,
  format,
  isSameDay,
  startOfMonth
} from 'date-fns'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { updateTask } from '@/db/space-tasks'
import { updateSprint } from '@/db/space-sprints'
import { getPlanningDateSpan } from '@/lib/spaces/planning-date-spans'

interface TimelineViewProps {
  tasks?: SpaceTask[]
  sprints?: SpaceSprint[]
  onTaskClick?: (task: SpaceTask) => void
  onSprintClick?: (sprint: SpaceSprint) => void
  onTaskUpdate?: (task: SpaceTask) => void
  onSprintUpdate?: (sprint: SpaceSprint) => void
}

type TimelineItem = {
  id: string
  title: string
  type: 'task' | 'sprint'
  startDate: Date
  endDate: Date
  dayCount: number
  color: string
  data: SpaceTask | SpaceSprint
}

type PositionedTimelineItem = {
  item: TimelineItem
  lane: number
  left: number
  width: number
}

const DAY_COLUMN_WIDTH = 40
const MONTH_HEADER_HEIGHT = 36
const DAY_HEADER_HEIGHT = 36
const ITEM_HEIGHT = 56
const ITEM_GAP = 12
const ITEM_TOP_PADDING = 12
const EMPTY_TIMELINE_HEIGHT = 220
const MIN_TIMELINE_WIDTH = 960

export const TimelineView: FC<TimelineViewProps> = ({
  tasks = [],
  sprints = [],
  onTaskClick,
  onSprintClick,
  onTaskUpdate,
  onSprintUpdate
}) => {
  const timelineItems = useMemo<TimelineItem[]>(() => {
    const items: TimelineItem[] = []

    // Add tasks with start/end ranges
    tasks.forEach((task) => {
      const span = getPlanningDateSpan(task.start_date, task.due_date)
      if (!span) return

      items.push({
        id: task.id,
        title: task.title,
        type: 'task',
        startDate: span.startDate,
        endDate: span.endDate,
        dayCount: span.dayCount,
        color: 'bg-blue-500',
        data: task
      })
    })

    // Add sprints with date ranges
    sprints.forEach((sprint) => {
      const span = getPlanningDateSpan(sprint.start_date, sprint.end_date)
      if (!span) return

      items.push({
        id: sprint.id,
        title: sprint.name,
        type: 'sprint',
        startDate: span.startDate,
        endDate: span.endDate,
        dayCount: span.dayCount,
        color: 'bg-purple-500',
        data: sprint
      })
    })

    // Sort by start date
    return items.sort((a, b) => {
      const startDiff = a.startDate.getTime() - b.startDate.getTime()
      if (startDiff !== 0) return startDiff

      return b.endDate.getTime() - a.endDate.getTime()
    })
  }, [tasks, sprints])

  // Calculate the date range for display
  const dateRange = useMemo(() => {
    if (timelineItems.length === 0) {
      const today = new Date()
      return {
        start: startOfMonth(today),
        end: endOfMonth(today)
      }
    }

    const allDates = timelineItems.flatMap((item) => [item.startDate, item.endDate])
    const minDate = new Date(Math.min(...allDates.map((d) => d.getTime())))
    const maxDate = new Date(Math.max(...allDates.map((d) => d.getTime())))

    // Add some padding
    const start = startOfMonth(minDate)
    const end = endOfMonth(maxDate)

    return { start, end }
  }, [timelineItems])

  const days = useMemo(
    () => eachDayOfInterval({ start: dateRange.start, end: dateRange.end }),
    [dateRange.end, dateRange.start]
  )

  const timelineGridRef = useRef<HTMLDivElement | null>(null)
  const totalDays = Math.max(1, days.length)
  const today = new Date()

  const monthSegments = useMemo(() => {
    const segments: Array<{ label: string; left: number; width: number }> = []
    let startIndex = 0

    while (startIndex < days.length) {
      const label = format(days[startIndex], 'MMM yyyy')
      let endIndex = startIndex

      while (endIndex + 1 < days.length && format(days[endIndex + 1], 'MMM yyyy') === label) {
        endIndex += 1
      }

      segments.push({
        label,
        left: startIndex * DAY_COLUMN_WIDTH,
        width: (endIndex - startIndex + 1) * DAY_COLUMN_WIDTH
      })

      startIndex = endIndex + 1
    }

    return segments
  }, [days])

  const positionedItems = useMemo<PositionedTimelineItem[]>(() => {
    const laneEndOffsets: number[] = []

    return timelineItems.map((item) => {
      const startOffset = Math.max(0, differenceInCalendarDays(item.startDate, dateRange.start))
      const durationDays = item.dayCount
      const endOffset = startOffset + durationDays - 1

      let lane = laneEndOffsets.findIndex((existingLaneEnd) => startOffset > existingLaneEnd)

      if (lane === -1) {
        lane = laneEndOffsets.length
        laneEndOffsets.push(endOffset)
      } else {
        laneEndOffsets[lane] = endOffset
      }

      return {
        item,
        lane,
        left: startOffset * DAY_COLUMN_WIDTH,
        width: durationDays * DAY_COLUMN_WIDTH
      }
    })
  }, [dateRange.start, timelineItems])

  const laneCount = useMemo(
    () =>
      positionedItems.reduce(
        (maxLane, positionedItem) => Math.max(maxLane, positionedItem.lane + 1),
        0
      ),
    [positionedItems]
  )

  const timelineBodyHeight =
    timelineItems.length === 0
      ? EMPTY_TIMELINE_HEIGHT
      : Math.max(
          EMPTY_TIMELINE_HEIGHT,
          ITEM_TOP_PADDING * 2 + laneCount * ITEM_HEIGHT + Math.max(0, laneCount - 1) * ITEM_GAP
        )

  const timelineContentWidth = Math.max(MIN_TIMELINE_WIDTH, totalDays * DAY_COLUMN_WIDTH)

  const handleItemClick = (item: TimelineItem) => {
    if (item.type === 'task' && onTaskClick) {
      onTaskClick(item.data as SpaceTask)
    } else if (item.type === 'sprint' && onSprintClick) {
      onSprintClick(item.data as SpaceSprint)
    }
  }

  type TimelineDragMode = 'move' | 'resize-start' | 'resize-end'
  type TimelineDragPayload = {
    id: string
    type: 'task' | 'sprint'
    mode: TimelineDragMode
  }

  const handleTimelineDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const raw = e.dataTransfer.getData('text/plain')
    if (!raw) return

    let payload: TimelineDragPayload
    try {
      payload = JSON.parse(raw) as TimelineDragPayload
    } catch {
      return
    }

    const container = timelineGridRef.current
    if (!container) return

    const rect = container.getBoundingClientRect()
    if (rect.width <= 0) return

    const x = e.clientX - rect.left
    const targetIndex = Math.min(totalDays - 1, Math.max(0, Math.floor(x / DAY_COLUMN_WIDTH)))
    const targetDate = addDays(dateRange.start, targetIndex)

    const item = timelineItems.find(
      (candidate) => candidate.id === payload.id && candidate.type === payload.type
    )
    if (!item) return

    const oldStart = item.startDate
    const oldEnd = item.endDate
    const durationDays = Math.max(0, differenceInCalendarDays(oldEnd, oldStart))

    let newStart = oldStart
    let newEnd = oldEnd

    if (payload.mode === 'move') {
      newStart = targetDate
      newEnd = addDays(newStart, durationDays)
    } else if (payload.mode === 'resize-start') {
      newStart = targetDate
    } else if (payload.mode === 'resize-end') {
      newEnd = targetDate
    }

    if (newEnd.getTime() < newStart.getTime()) {
      newEnd = newStart
    }

    try {
      if (payload.type === 'task') {
        const task = item.data as SpaceTask
        const updated = await updateTask(payload.id, {
          space_id: task.space_id,
          start_date: newStart.toISOString(),
          due_date: newEnd.toISOString()
        })
        onTaskUpdate?.(updated)
      } else {
        const updated = await updateSprint(payload.id, {
          start_date: newStart.toISOString().slice(0, 10),
          end_date: newEnd.toISOString().slice(0, 10)
        })
        onSprintUpdate?.(updated as SpaceSprint)
      }

      toast.success('Timeline updated')
    } catch {
      toast.error('Failed to update timeline')
    }
  }

  return (
    <div className="space-y-6">
      <div className="overflow-auto rounded-xl border">
        <div
          ref={timelineGridRef}
          className="bg-background min-w-full"
          style={{ width: `${timelineContentWidth}px` }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleTimelineDrop}
        >
          <div className="relative border-b" style={{ height: `${MONTH_HEADER_HEIGHT}px` }}>
            {monthSegments.map((segment) => (
              <div
                key={segment.label}
                className="border-border/60 absolute top-0 flex h-full items-center justify-center border-r px-3 text-center"
                style={{
                  left: `${segment.left}px`,
                  width: `${segment.width}px`
                }}
              >
                <p className="text-muted-foreground text-sm font-semibold">{segment.label}</p>
              </div>
            ))}
          </div>

          <div className="relative border-b" style={{ height: `${DAY_HEADER_HEIGHT}px` }}>
            {days.map((day, index) => (
              <div
                key={day.toISOString()}
                className={cn(
                  'border-border/60 absolute inset-y-0 border-r',
                  isSameDay(day, today) && 'bg-blue-50 dark:bg-blue-900/10'
                )}
                style={{
                  left: `${index * DAY_COLUMN_WIDTH}px`,
                  width: `${DAY_COLUMN_WIDTH}px`
                }}
              >
                <div className="text-muted-foreground flex h-full items-center justify-center text-xs">
                  {format(day, 'd')}
                </div>
              </div>
            ))}
          </div>

          <div className="relative" style={{ height: `${timelineBodyHeight}px` }}>
            {days.map((day, index) => (
              <div
                key={`grid-${day.toISOString()}`}
                className={cn(
                  'border-border/60 absolute inset-y-0 border-r',
                  isSameDay(day, today) && 'bg-blue-50/70 dark:bg-blue-900/10'
                )}
                style={{
                  left: `${index * DAY_COLUMN_WIDTH}px`,
                  width: `${DAY_COLUMN_WIDTH}px`
                }}
              />
            ))}

            {positionedItems.map((positionedItem) => {
              const { item, lane, left, width } = positionedItem
              const showTitle = width >= 72
              const showMeta = width >= 148
              const showBadge = width >= 120

              return (
                <div
                  key={item.id}
                  className="absolute z-10 px-1"
                  style={{
                    top: `${ITEM_TOP_PADDING + lane * (ITEM_HEIGHT + ITEM_GAP)}px`,
                    left: `${left}px`,
                    width: `${width}px`
                  }}
                  title={`${item.title} (${format(item.startDate, 'MMM d')} - ${format(item.endDate, 'MMM d')})`}
                >
                  <Card
                    className={cn(
                      'border-border h-14 cursor-move overflow-hidden transition-all hover:shadow-md',
                      item.type === 'sprint' && 'border-purple-300 dark:border-purple-700'
                    )}
                    onClick={() => handleItemClick(item)}
                    draggable
                    onDragStart={(e) => {
                      const payload: TimelineDragPayload = {
                        id: item.id,
                        type: item.type,
                        mode: 'move'
                      }
                      e.dataTransfer.setData('text/plain', JSON.stringify(payload))
                      e.dataTransfer.effectAllowed = 'move'
                    }}
                  >
                    <CardContent className="relative flex h-full items-center gap-2 overflow-hidden p-2">
                      <div className={cn('absolute inset-y-0 left-0 w-1', item.color)} />
                      <div
                        className="bg-foreground/5 absolute left-0 top-0 h-full w-2 cursor-ew-resize"
                        draggable
                        onClick={(evt) => evt.stopPropagation()}
                        onDragStart={(e) => {
                          e.stopPropagation()
                          const payload: TimelineDragPayload = {
                            id: item.id,
                            type: item.type,
                            mode: 'resize-start'
                          }
                          e.dataTransfer.setData('text/plain', JSON.stringify(payload))
                          e.dataTransfer.effectAllowed = 'move'
                        }}
                      />
                      <div
                        className="bg-foreground/5 absolute right-0 top-0 h-full w-2 cursor-ew-resize"
                        draggable
                        onClick={(evt) => evt.stopPropagation()}
                        onDragStart={(e) => {
                          e.stopPropagation()
                          const payload: TimelineDragPayload = {
                            id: item.id,
                            type: item.type,
                            mode: 'resize-end'
                          }
                          e.dataTransfer.setData('text/plain', JSON.stringify(payload))
                          e.dataTransfer.effectAllowed = 'move'
                        }}
                      />
                      {item.type === 'task' ? (
                        <IconCheckbox size={16} className="shrink-0 text-blue-500" />
                      ) : (
                        <IconCalendar size={16} className="shrink-0 text-purple-500" />
                      )}
                      {showTitle && (
                        <div className="min-w-0 flex-1">
                          <p className="text-foreground truncate text-xs font-medium">
                            {item.title}
                          </p>
                          {showMeta && (
                            <div className="text-muted-foreground flex items-center gap-1 text-[10px]">
                              <IconCalendar size={10} />
                              <span>
                                {format(item.startDate, 'MMM d')}
                                {!isSameDay(item.startDate, item.endDate) &&
                                  ` - ${format(item.endDate, 'MMM d')}`}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                      {showBadge && (
                        <Badge variant="secondary" className="text-[10px]">
                          {item.type}
                        </Badge>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )
            })}

            {timelineItems.length === 0 && (
              <div className="flex h-full items-center justify-center p-8">
                <Card className="border-border border-dashed">
                  <CardContent className="p-8">
                    <p className="text-muted-foreground text-center text-sm">
                      No tasks or sprints with dates to display in timeline
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="border-border flex items-center gap-4 border-t pt-4">
        <div className="flex items-center gap-2">
          <div className="size-3 rounded bg-blue-500"></div>
          <span className="text-muted-foreground text-xs">Tasks</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="size-3 rounded bg-purple-500"></div>
          <span className="text-muted-foreground text-xs">Sprints</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="size-3 border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/10"></div>
          <span className="text-muted-foreground text-xs">Today</span>
        </div>
      </div>
    </div>
  )
}
